import { put, select, takeEvery } from "redux-saga/effects";
import { Action } from "redux";

import { closeConfirmModal, openConfirmModal } from "actions";

import { REPORT_JOB } from "constants/actionTypes";

import { getReportJobList, openReportJobsList } from "lib/navigation";
import toast from "lib/toast";

import history from "appHistory";
import { pluralize } from "lib/pluralize";
import { fetchWithFullyQualifiedUrl } from "lib/fetch";
import { getActiveRole } from "selectors";
import { ReportJobAction } from "../actions/reportJob";
import { takeActionOutcome } from "./lib";
import {
  BulkExportReportJobAction,
  BulkExportReportJobFailureAction,
  BulkExportReportJobSuccessAction,
  CompileReportJobAction,
} from "actions/reportJob.compile";

function* onBulkCreateReportJobsSuccess(action) {
  const { suppressSuccessPrompt } = action;
  if (suppressSuccessPrompt) {
    return;
  }
  if (!Array.isArray(action.payload)) {
    // payload should always be an array of report jobs.
    // If it's not, something is wrong. Still avoid a crash
    return;
  }
  const reportJobCount = action.payload.length;

  if (reportJobCount === 0) {
    toast.info("No Reports were created.");
    return;
  }

  if (history.location.pathname === getReportJobList()) {
    // Don't prompt the user to redirect when they are already on the Report Jobs page
    // They get a success toast and if they're on the page, AgGrid should show
    // them with the flashing on changes feature
    toast.success(
      `${reportJobCount} ${pluralize("Report", reportJobCount)} created.`,
    );
    return;
  }

  yield put(
    openConfirmModal({
      actions: [
        { label: "Close", secondary: true, onClickAction: closeConfirmModal },
        {
          label: "View Reports",
          onClickAction: () => {
            openReportJobsList();
            return closeConfirmModal();
          },
        },
      ],
      title: `${pluralize("Report", reportJobCount)} Created`,
      message: `${reportJobCount} ${pluralize("Report", reportJobCount)} have been created. You can view them on the Reports page.`,
    }) as Action,
  );
}

function* onCompileReportJobAction(action: CompileReportJobAction) {
  const { reportJobIds } = action;

  yield put(ReportJobAction.bulkExport(reportJobIds));

  yield takeActionOutcome(
    REPORT_JOB.BULK_EXPORT.SUCCESS,
    REPORT_JOB.BULK_EXPORT.FAILURE,
    (compileSucceededAction: BulkExportReportJobSuccessAction) => {
      if (compileSucceededAction.successUrl) {
        window.open(compileSucceededAction.successUrl, "_blank");
      } else {
        toast.error(`Could not open compiled PDF`);
      }
    },
    (compileFailedAction: BulkExportReportJobFailureAction) => {
      toast.error(
        `Bulk export responded with code: ${compileFailedAction.status} (${compileFailedAction.statusText})`,
      );
    },
  );
}

function* onBulkExportReportJobRequest(action: BulkExportReportJobAction) {
  const { reportJobIds } = action;

  const state = yield select();
  const userRoleSlug: string = getActiveRole(state).slug;

  const response: Response = yield fetchWithFullyQualifiedUrl(
    `/v2/report-jobs/bulk-export/?user_role=${userRoleSlug}`,
    {
      headers: {
        Accept: "text/html",
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({
        report_job_ids: reportJobIds,
      }),
    },
  );

  // Response is redirect - this is a success
  if (response.redirected) {
    yield put({
      type: REPORT_JOB.BULK_EXPORT.SUCCESS,
      successUrl: response.url,
    });
  } else {
    yield put({
      type: REPORT_JOB.BULK_EXPORT.FAILURE,
      status: response.status,
      statusText: response.statusText,
    });
  }
}

export default function* reportJobsSaga() {
  yield takeEvery(
    REPORT_JOB.CREATE_BULK.SUCCESS,
    onBulkCreateReportJobsSuccess,
  );
  yield takeEvery(REPORT_JOB.COMPILE.ACTION, onCompileReportJobAction);
  yield takeEvery(REPORT_JOB.BULK_EXPORT.REQUEST, onBulkExportReportJobRequest);
}
